import { Capacitor } from "@capacitor/core";
import { Health } from "@capgo/capacitor-health";

function startOfTodayIso() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.toISOString();
}

function nowIso() {
  return new Date().toISOString();
}

export function isAndroidNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export function isIosNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export function isNativeMobile() {
  return Capacitor.isNativePlatform();
}

/**
 * Returns availability info for the health data source on the current platform.
 * Android → Health Connect
 * iOS     → Apple HealthKit (always available on device)
 */
export async function getHealthConnectAvailability() {
  if (!Capacitor.isNativePlatform()) {
    return { available: false, reason: "Step sync is available on Android and iOS app builds only." };
  }

  if (isIosNative()) {
    // HealthKit is always present on iOS — just report available
    return { available: true, reason: "" };
  }

  // Android: check Health Connect
  try {
    const status = await Health.isAvailable();
    return { available: Boolean(status.available), reason: status.reason || "" };
  } catch (error) {
    return { available: false, reason: error?.message || "Could not check Health Connect availability." };
  }
}

/**
 * Request read permission for steps.
 * Uses the same @capgo/capacitor-health API on both platforms.
 */
export async function requestStepsPermission() {
  try {
    const status = await Health.requestAuthorization({ read: ["steps"], write: [] });
    return {
      granted: Array.isArray(status.readAuthorized) && status.readAuthorized.includes("steps"),
      denied: status.readDenied || [],
    };
  } catch (error) {
    return { granted: false, denied: [error?.message || "Permission request failed."] };
  }
}

/**
 * Read total step count for a specific date (YYYY-MM-DD).
 * Tries aggregated query first, falls back to summing raw samples.
 */
export async function readStepsForDate(dateStr) {
  const start = new Date(dateStr + "T00:00:00");
  const end = new Date(dateStr + "T00:00:00");
  end.setDate(end.getDate() + 1);
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  try {
    const { samples } = await Health.queryAggregated({
      dataType: "steps",
      startDate: startIso,
      endDate: endIso,
      bucket: "day",
      aggregation: "sum",
    });
    const total = Number(samples?.[0]?.value || 0);
    return { ok: true, steps: Number.isFinite(total) ? total : 0 };
  } catch {
    try {
      const { samples } = await Health.readSamples({
        dataType: "steps",
        startDate: startIso,
        endDate: endIso,
        limit: 1000,
      });
      const total = (samples || []).reduce((sum, s) => sum + Number(s?.value || 0), 0);
      return { ok: true, steps: Number.isFinite(total) ? total : 0 };
    } catch (error) {
      return { ok: false, steps: 0, reason: error?.message || "Unable to read steps." };
    }
  }
}

/**
 * Read today's total step count.
 * Tries aggregated query first, falls back to summing raw samples.
 */
export async function readTodayStepsTotal() {
  try {
    const { samples } = await Health.queryAggregated({
      dataType: "steps",
      startDate: startOfTodayIso(),
      endDate: nowIso(),
      bucket: "day",
      aggregation: "sum",
    });

    const total = Number(samples?.[0]?.value || 0);
    return { ok: true, steps: Number.isFinite(total) ? total : 0 };
  } catch {
    try {
      const { samples } = await Health.readSamples({
        dataType: "steps",
        startDate: startOfTodayIso(),
        endDate: nowIso(),
        limit: 1000,
      });
      const total = (samples || []).reduce((sum, sample) => sum + Number(sample?.value || 0), 0);
      return { ok: true, steps: Number.isFinite(total) ? total : 0 };
    } catch (error) {
      return { ok: false, steps: 0, reason: error?.message || "Unable to read steps." };
    }
  }
}
